import { useState } from 'react';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { useActiveUserContext } from '@vivli/core/infrastructure/context';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { DataRequestStatusEnum, VmAdminApprovalStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { BlockMessageComponent } from '../block-message.component';
import { EsignComponent } from '../esign.component';
import { ContinueComponent } from '@vivli/shared/components';
import { VmStatusEnum } from '@vivli/features/virtual-machine/infrastructure/enum';

const environmentMessages = {
    notAvailable: 'SECURE RESEARCH ENVIRONMENT IS NOT AVAILABLE FOR THIS DATA REQUEST UNTIL IT IS PARTIALLY FULFILLED.',
    notAuthorized: 'YOU ARE NOT AUTHORIZED TO REQUEST A SECURE RESEARCH ENVIRONMENT FOR THIS DATA REQUEST.',
    notDuaApproved: 'PLEASE CONTACT THE VIVLI ADMIN TO GRANT YOU ACCESS TO THE SECURE RESEARCH ENVIRONMENT FOR THIS DATA REQUEST.',
    notSigned: 'YOU MUST E-SIGN THE VIVLI TERMS OF USE AGREEMENT TO CONTINUE.',
    notSignedInProcess:
        'YOU MUST E-SIGN THE VIVLI TERMS OF USE AGREEMENT TO CONTINUE. PROCESSING THIS MAY TAKE SOME TIME; PLEASE RETURN IN A WHILE.',
    notProvisioned: 'WAITING FOR THE LEAD INVESTIGATOR TO INITIATE THE SECURE RESEARCH ENVIRONMENT',
    downloadableDataOnly: `The data package for all the studies in this data request are available for download for analysis
            on your own computer(s).  You do not need to start a Secure Research Environment in order to download the data package(s).
            To download the data package, go to the studies tab within your data request, open each study and click on the
            'Download Data Package' subtab to download the files. Please see the section 'Downloadable data' in this user guide:
            `,
};

const environmentUrls = {
    downloadableDataOnly: 'https://vivli.org/how-to-access-data-from-vivli-use-the-secure-research-environment.',
};

const continueToVmMessage = 'Press Continue if you still plan to use a Secure Research Environment.';

export const VmStatusGuardComponent = ({ children }) => {
    const { dataRequest, virtualMachine } = useDataRequestContext();
    const { user } = useActiveUserContext();
    const [hasSeenDownloadableMessage, setHasSeenDownloadableMessage] = useState<boolean>(false);

    if (!dataRequest) {
        return <LoadIndicatorCenteredView />;
    }

    const isProvisioned = !!virtualMachine && virtualMachine.status !== VmStatusEnum.None;
    const userDetails = dataRequest.researchTeam.researchTeamDetails.find((x) => x.userId === user.userId);

    const isPartiallyFulfilledOrFulfilled =
        dataRequest.status === DataRequestStatusEnum.Fulfilled || dataRequest.status === DataRequestStatusEnum.PartiallyFulfilled;
    const isDuaApproved =
        userDetails?.adminApprovalStatus === VmAdminApprovalStatusEnum.Approved ||
        userDetails?.adminApprovalStatus === VmAdminApprovalStatusEnum.Automatic;
    const isSigned = dataRequest?.requirementsAndAttestations?.userIDsHaveAgreedToDataUse.some((x) => x === user.userId);
    const downloadableOnly = !dataRequest?.requestedStudies?.some((x) => x.downloadableRequestedStudyIPDDataPackage === false);

    if (!isPartiallyFulfilledOrFulfilled) {
        return <BlockMessageComponent message={environmentMessages.notAvailable} />;
    }

    if (!isProvisioned && !userDetails?.isLeadResearcher) {
        // admin is automatically granted access to research envs, and does not get hasAccessToVirtualMachineResource
        if (userDetails?.hasAccessToVirtualMachineResource || userDetails?.isAdmin) {
            if (!hasSeenDownloadableMessage && downloadableOnly) {
                return (
                    <BlockMessageComponent message={environmentMessages.downloadableDataOnly} url={environmentUrls.downloadableDataOnly}>
                        <ContinueComponent
                            userAcknowledged={function (): void {
                                setHasSeenDownloadableMessage(true);
                            }}
                            messageToUser={continueToVmMessage}
                        />
                    </BlockMessageComponent>
                );
            }
            return <BlockMessageComponent message={environmentMessages.notProvisioned} />;
        }

        return <BlockMessageComponent message={environmentMessages.notAuthorized} />;
    }

    if (!isDuaApproved) {
        return <BlockMessageComponent message={environmentMessages.notDuaApproved} />;
    }

    if (!isSigned) {
        if (!hasSeenDownloadableMessage && downloadableOnly) {
            return (
                <BlockMessageComponent message={environmentMessages.downloadableDataOnly} url={environmentUrls.downloadableDataOnly}>
                    <ContinueComponent
                        userAcknowledged={function (): void {
                            setHasSeenDownloadableMessage(true);
                        }}
                        messageToUser={continueToVmMessage}
                    />
                </BlockMessageComponent>
            );
        }

        return (
            <BlockMessageComponent message={environmentMessages.notSigned}>
                <EsignComponent />
            </BlockMessageComponent>
        );
    }

    return children;
};
